import { InfoTooltip } from "@/components/design/InfoTooltip";
import { SkillLevelScale } from "@/components/design/SkillLevelScale";
import { useEffect, useId, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { EscoSearchDocument, EscoOccupationSkillsDocument, EscoKind } from "@/graphql/generated/operations";
import { useDebounced } from "@/lib/use-debounced";
import type { EscoConcept, EscoLanguage } from "@/lib/esco";
import { Button } from "@/components/design/Button";

export function EscoSearch({ text, kind, language, onSelect, selected = [], existingLabels = [] }: {
  text: string; kind: "occupation" | "skill"; language: EscoLanguage; onSelect: (concept: EscoConcept) => void; selected?: readonly string[]; existingLabels?: readonly string[];
}) {
  const term = useDebounced(text.trim(), 400);
  const { data, loading, error, refetch } = useQuery(EscoSearchDocument, {
    variables: { input: { text: term.slice(0, 200), kind: kind === "occupation" ? EscoKind.Occupation : EscoKind.Skill, language } },
    skip: term.length < 2,
  });
  if (term.length < 2) return null;
  return <EscoResults key={`${kind}:${language}:${term}`} concepts={data?.escoSearch.concepts ?? []} unavailable={!!error || data?.escoSearch.status === "UNAVAILABLE"}
    loading={loading || term !== text.trim()} hasMore={data?.escoSearch.hasNextPage} onRetry={() => void refetch()} selected={selected} existingLabels={existingLabels} onSelect={onSelect} />;
}
export function EscoOccupationSkills({ uri, language, onSelect, onPreview, selected = [], existingLabels = [], filter = "" }: {
  uri: string; language: EscoLanguage; filter?: string; onSelect: (concept: EscoConcept, level: number) => void;
  onPreview?: (preview: { description: string; level: number } | null) => void; selected?: readonly string[]; existingLabels?: readonly string[];
}) {
  const { data, loading, error, refetch } = useQuery(EscoOccupationSkillsDocument, {
    variables: { uri, language },
  });
  return <EscoResults compact selectLevel onPreview={onPreview} key={`${uri}:${language}:${filter}`} concepts={(data?.escoOccupationSkills.concepts ?? []).filter(c => c.label.toLocaleLowerCase().includes(filter.trim().toLocaleLowerCase()))} loading={loading}
    unavailable={!!error || data?.escoOccupationSkills.status === "UNAVAILABLE"} hasMore={data?.escoOccupationSkills.hasNextPage}
    onRetry={() => void refetch()} selected={selected} existingLabels={existingLabels} onSelect={onSelect} />;
}
function EscoResults({ concepts, loading, unavailable, hasMore, onRetry, selected, onSelect, existingLabels = [], compact = false, selectLevel = false, onPreview }: {
  compact?: boolean; concepts: readonly EscoConcept[]; loading: boolean; unavailable: boolean; hasMore?: boolean; onRetry: () => void;
  existingLabels?: readonly string[]; selected: readonly string[]; onSelect: (concept: EscoConcept, level: number) => void;
  selectLevel?: boolean; onPreview?: (preview: { description: string; level: number } | null) => void;
}) {
  const id = useId();
  const intl = useIntl();
  const [choosing, setChoosing] = useState<string | null>(null);
  const pageSize = compact ? 3 : 5;
  const [visible, setVisible] = useState(pageSize);
  const alreadyPresent = (c: EscoConcept) => selected.includes(c.uri)
    || existingLabels.some(label => label.toLocaleLowerCase() === c.label.toLocaleLowerCase());
  if (loading) return <p role="status" className="smycles-field-meta"><FormattedMessage id="esco.loading" defaultMessage="Searching ESCO…" /></p>;
  if (unavailable) return <div role="status" className="space-y-2 smycles-field-meta">
    <p><FormattedMessage id="esco.unavailable" defaultMessage="ESCO is currently unavailable. You can continue with your own wording." /></p>
    <Button id={`${id}-retry`} variant="ghost" size="sm" onClick={onRetry}><FormattedMessage id="suggestions.retry" defaultMessage="Retry" /></Button>
  </div>;
  return <div className="space-y-3">
    {concepts.length === 0 ? <p className="smycles-field-meta"><FormattedMessage id="esco.empty" defaultMessage="No matching ESCO concept. Try another term or keep your own wording." /></p> :
      <div id={`${id}-results`} role="region" aria-label={intl.formatMessage({ id: "esco.results", defaultMessage: "ESCO results" })}
        tabIndex={0} className={`box-flat bg-surface border border-ink-ui divide-y divide-ink/10 overflow-y-auto overscroll-contain [scrollbar-gutter:stable] px-2 py-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink-ui ${compact ? (choosing ? "max-h-96" : "max-h-48") : "max-h-80"}`}>
        {concepts.slice(0, visible).map(c => <div key={c.uri} className="py-2 first:pt-0">
          <div className="flex items-center gap-2">
          <button id={`${id}-select-${c.uri.split("/").at(-1)}`} type="button" disabled={alreadyPresent(c)} aria-expanded={selectLevel ? choosing === c.uri : undefined}
            onClick={() => selectLevel ? setChoosing(current => current === c.uri ? null : c.uri) : onSelect(c, 0)}
            className="flex min-w-0 flex-1 items-start justify-between gap-3 rounded px-2 py-2 text-left text-smycles-body text-ink hover:bg-ink/5 focus-visible:outline-2 focus-visible:outline-ink-ui disabled:text-ink-muted">
            <span>{c.label}</span><span className="shrink-0" aria-hidden="true">{alreadyPresent(c) ? "✓" : "+"}</span>
          </button>
          {c.description?.trim() ? <InfoTooltip id={`${id}-description-${c.uri.split("/").at(-1)}`}
            ariaLabel={`${intl.formatMessage({ id: "esco.details", defaultMessage: "Description" })}: ${c.label}`}
            title={c.label} placement="bottom" className="shrink-0">
            <span className="block max-h-64 overflow-y-auto whitespace-pre-line break-words">{c.description}</span>
          </InfoTooltip> : null}
          </div>
          {selectLevel && choosing === c.uri && !alreadyPresent(c) ? <EscoSkillLevelChoice concept={c} onPreview={onPreview}
            onSelect={level => { onSelect(c, level); setChoosing(null); }} onCancel={() => setChoosing(null)} /> : null}
        </div>)}
      </div>}
    {visible < concepts.length ? <Button id={`${id}-more`} variant="ghost" size="sm" onClick={() => setVisible(n => n + pageSize)}>
      <FormattedMessage id="esco.more" defaultMessage="Show more" />
    </Button> : hasMore ? <p className="smycles-field-meta"><FormattedMessage id="esco.refine" defaultMessage="More results available. Refine your search." /></p> : null}
    <p className="smycles-field-meta"><a href="https://esco.ec.europa.eu" target="_blank" rel="noreferrer" className="underline">
      <FormattedMessage id="esco.source" defaultMessage="Source: ESCO · European Commission" />
    </a></p>
  </div>;
}

function EscoSkillLevelChoice({ concept, onSelect, onCancel, onPreview }: {
  concept: EscoConcept; onSelect: (level: number) => void; onCancel: () => void;
  onPreview?: (preview: { description: string; level: number } | null) => void;
}) {
  const id = useId();
  useEffect(() => () => onPreview?.(null), [onPreview]);
  const preview = (level: number | null) => onPreview?.(level === null ? null : { description: concept.label, level });
  return <div className="space-y-1 px-2 pb-2 pt-2" onKeyDown={e => { if (e.key === "Escape") onCancel(); }}>
    <SkillLevelScale value={0} idPrefix={`${id}-level`} onChange={onSelect} onPreview={preview} />
    <div className="flex flex-wrap gap-2">
      <Button id={`${id}-unrated`} variant="chip" size="sm" onClick={() => onSelect(0)}
        onMouseEnter={() => preview(0)} onMouseLeave={() => preview(null)} onFocus={() => preview(0)} onBlur={() => preview(null)}>
        <FormattedMessage id="esco.addUnrated" defaultMessage="Add unrated" />
      </Button>
      <Button id={`${id}-cancel`} variant="ghost" size="sm" onClick={onCancel}>
        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
      </Button>
    </div>
  </div>;
}
